import { create } from "zustand";
import { devtools } from "zustand/middleware";

import { NO_META } from "@/constants";
import {
  createComment,
  deleteCommentById,
  getAnswersByCommentId,
  getComment,
  getCommentsByContentId,
} from "@services/http/members";

import {
  CreateCommentInput,
  DeleteCommentByIdInput,
  GetAnswerByCommentIdInput,
  GetCommentFiltersOptionsInputList,
  GetCommentsByContentIdInput,
} from "@_types/comments";
import { ListCommentsStoreActions, ListCommentsStoreProps } from "./Comment.types";

export const useCommentStore = create<ListCommentsStoreProps & ListCommentsStoreActions>()(
  devtools((set, get) => ({
    commentList: [],
    commentDetail: undefined,
    meta: NO_META,
    params: {
      pageSize: 10,
      page: 1,
    },
    isLoading: false,
    isUpdatingDetails: false,
    activeFilters: [],
    currentReplies: [],

    setComments: (data, metaData) => set({ commentList: data, meta: metaData }),

    setCommentDetail: data => set({ commentDetail: data }),

    clearCurrentReplies: () => set({ currentReplies: [] }),

    fetchComment: async incomingParams => {
      set({ isLoading: true });
      const { params } = get();
      try {
        const { data: response } = await getComment({ ...params, ...incomingParams });
        set({ commentList: response.data, meta: response.meta });
      } catch (error) {
        set({ commentList: [], meta: NO_META });
      } finally {
        set({ isLoading: false });
      }
    },

    updateCommentList: async () => {
      const { params } = get();
      try {
        const { data: response } = await getComment({ ...params });
        set({ commentList: response.data, meta: response.meta });
      } catch (error) {
        set({ commentList: [], meta: NO_META });
      }
    },

    setIsLoading: state => set({ isLoading: state }),

    setIsUpdatingDetails: state => set({ isUpdatingDetails: state }),

    paginate: page => {
      const { params, fetchComment } = get();
      set({ params: { ...params, page } });
      fetchComment();
    },

    search: query => {
      const { params, fetchComment } = get();
      set({ params: { ...params, search: query } });
      query.length === 0 && set({ params: { ...params, search: undefined } });
      fetchComment();
    },

    addFilter: newFilter => {
      const { activeFilters, filterToParams } = get();
      const foundFilter = activeFilters.find(filter => filter.field === newFilter.field);
      if (foundFilter !== undefined) {
        set({
          activeFilters: [...activeFilters.filter(item => item.field !== newFilter.field), newFilter],
        });
      } else {
        set({
          activeFilters: [...activeFilters, newFilter],
        });
      }
      filterToParams();
    },

    getFilter: field => {
      const { activeFilters } = get();
      return activeFilters.find(filter => filter.field === field);
    },

    removeFilter: index => {
      const { activeFilters, filterToParams } = get();
      const newFilters = activeFilters.filter((_, i) => i !== index);
      set({ activeFilters: newFilters });
      filterToParams();
    },

    removeFilterByName: field => {
      const { activeFilters, filterToParams } = get();
      let newFilters = activeFilters.filter(item => item.field !== field);
      if (field === "createdAtFromDate") {
        newFilters = newFilters.filter(item => item.field !== "createdAtToDate");
      }
      set({ activeFilters: newFilters });
      filterToParams();
    },

    clearFilters: () => {
      const { filterToParams } = get();
      set({ activeFilters: [] });
      filterToParams();
    },

    filterToParams: () => {
      const { activeFilters, params } = get();
      if (activeFilters.length > 0) {
        const filterParams: Record<GetCommentFiltersOptionsInputList, string> = activeFilters.reduce(
          (acc, { field, value }) => {
            acc[field] = value;
            return acc;
          },
          {} as Record<GetCommentFiltersOptionsInputList, string>
        );
        set({ params: { ...params, ...filterParams } });
      } else {
        set({ params: { page: params.page, pageSize: params.pageSize, search: params.search } });
      }
    },

    createCommentOrAnswer: async (commentData: CreateCommentInput) => {
      try {
        const response = await createComment(commentData);

        set(prev => ({
          commentList: [...prev.commentList, response.data],
        }));
      } catch (error) {}
    },

    fetchAnswersByCommentId: async ({ uuid }: GetAnswerByCommentIdInput) => {
      try {
        const { data: replies } = await getAnswersByCommentId({ uuid });

        set({ currentReplies: replies.data });
      } catch (error) {
        console.error("Error in Comments - ANSWERS", error);
      }
    },

    resetParams: incomingParams => {
      const { params } = get();
      set({ params: { ...params, ...incomingParams } });
    },

    deleteCommentById: async ({ uuid }: DeleteCommentByIdInput) => {
      try {
        await deleteCommentById({ uuid });
        set(state => ({
          commentList: state.commentList.filter(comment => comment.uuid !== uuid),
        }));
      } catch (error) {
        console.error("Error in Comments - DELETE", error);
      }
    },

    fetchCommentsByContentId: async ({ contentUuid }: GetCommentsByContentIdInput) => {
      try {
        const { data: list } = await getCommentsByContentId({ contentUuid });

        set({ commentList: list.data });
      } catch (error) {
        console.error("Error in Comments - By Content ID", error);
      }
    },
  }))
);
